import { InjectProperty } from '@jkhy/frontend-utils/di';
import { AppType, ConfigurationService } from '@treasury/core/config';
import { NavigationService } from '@treasury/core/navigation';
import { NotificationService, TmBaseComponent } from '@treasury/presentation';
import { TmFormSubmitted } from '@treasury/presentation/components/forms';
import '@treasury/presentation/components/forms/tm-form';
import '@treasury/presentation/components/tm-blocking-loader';
import '@treasury/presentation/components/tm-body';
import '@treasury/presentation/components/tm-button';
import { TmButtonType } from '@treasury/presentation/components/tm-button.types';
import '@treasury/presentation/components/tm-email-field';
import '@treasury/presentation/components/tm-text-field';
import { css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LoginClient } from '../login-client';
import { FORGOT_PASSWORD_TAG_NAME } from './forgot-password.data';
import './partials/login-layout';

interface ForgotPasswordModel {
    companyId: string;
    loginId: string;
    email: string;
}

@customElement(FORGOT_PASSWORD_TAG_NAME)
export default class ForgotPassword extends TmBaseComponent {
    @state()
    private loading = false;

    @InjectProperty()
    declare private readonly client: LoginClient;

    @InjectProperty()
    declare private readonly config: ConfigurationService;

    @InjectProperty()
    declare private notificationService: NotificationService;

    @InjectProperty()
    declare private navigationService: NavigationService;

    private get isConsumer() {
        return this.config.app !== AppType.BackOffice;
    }

    protected render() {
        return html`
            ${this.renderBlockingLoader()}
            <login-layout
                headerText="Forgot Password"
                .stickyHeader=${this.isConsumer}
                .isConsumer=${this.isConsumer}
            >
                <slot slot="default-logo" name="default-logo"></slot>
                <span slot="phase">
                    <tm-form
                        @form-submitted=${(e: TmFormSubmitted<ForgotPasswordModel>) =>
                            this.onFormSubmitted(e)}
                    >
                        <tm-body id="content" .shadow=${false}>
                            <p>
                                If you have forgotten your password, you can reset it by verifying
                                the information below. You must be an active user and the
                                credentials entered below must match your user profile exactly. If
                                the information is entered correctly, an email will be sent to the
                                address on record.
                            </p>
                            <br />
                            <strong>
                                You will have two hours from the time the email is sent to take the
                                next steps.
                            </strong>
                            <tm-text-field
                                required
                                label=${this.isConsumer ? 'Company ID' : 'FI ID'}
                                class="pb-1"
                                autocapitalize="off"
                                .name=${'companyId'}
                            ></tm-text-field>
                            <tm-text-field
                                required
                                class="pb-1"
                                autocapitalize="off"
                                label="Login ID"
                                .name=${'loginId'}
                            ></tm-text-field>
                            <tm-email-field
                                required
                                label="Email"
                                class="pb-1"
                                autocapitalize="off"
                                .name=${'email'}
                            ></tm-email-field>
                            <div class="form-actions">
                                <tm-button .type=${TmButtonType.Submit} importance="primary"
                                    >Forgot Password</tm-button
                                >
                                <tm-button
                                    importance="tertiary"
                                    @click=${() => this.navigationService.navigate('/login')}
                                    >Return to Login</tm-button
                                >
                            </div>
                        </tm-body>
                    </tm-form>
                </span>
            </login-layout>
        `;
    }

    private async onFormSubmitted(e: TmFormSubmitted<ForgotPasswordModel>) {
        const { companyId, loginId, email } = e.detail.model as ForgotPasswordModel;
        try {
            this.loading = true;
            await this.client.forgotPassword({ fiId: companyId, loginId, email });
            this.notificationService.renderSuccess({
                header: 'Email Sent!',
                content: html`
                    <p class="mb-2">
                        Please check your email for further information about your password change.
                    </p>
                    <p>The instructions provided in the email will be valid for two hours.</p>
                `,
            });
        } catch (err) {
            this.notificationService.renderError(
                err instanceof Error
                    ? err
                    : 'An unknown error occurred. We could not reset your password. Please try again later.'
            );
        } finally {
            this.loading = false;
        }
    }

    private renderBlockingLoader() {
        if (!this.loading) {
            return nothing;
        }
        return html`<tm-blocking-loader></tm-blocking-loader>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                }

                #content {
                    padding-bottom: var(--sticky-header-height, 0);
                }

                .form-actions {
                    padding-top: 1em;
                    display: flex;
                    flex-direction: column;
                    gap: 0.5em;
                }

                @media only screen and (min-width: 640px) {
                    #content {
                        padding-bottom: 0px;
                    }
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [FORGOT_PASSWORD_TAG_NAME]: ForgotPassword;
    }
}
